import { ReactNode } from 'react';

import jwt_decode from 'jwt-decode';
import { generatePath } from 'react-router-dom';

import { DateTools, Utils } from '@treezor/design-system';

import i18n from '@/i18n';

import {
  statusColor,
  UserStatusLabel,
  EParentType,
  physicalUserRole,
  TPhysicalUserRole,
  IUser,
  selfEmployedLegalForms,
  TLegalFormKey,
  UserTypes,
} from '@/features/user/store/user.model';

import { IKycDocument } from '@/features/kyc/store/models/kyc.model';

import { IBeneficiary } from '@/features/beneficiaries/store/models/beneficiary.model';

import { TStatusColorKey, getStatusByLabel } from '@/types/colors.types';

import { store } from '@/store/configuration';
import { IApiError } from '@/store/types/base.state';

import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';

import { getRoutepaths } from '@/router/hooks/useRoutePaths';
import { TOption } from '@/utils/constants';
import { IAccessTokenDecoded } from '@/store/models/auth.model';

export const DashboardHelpers = {
  getAccessTokenExpiration(): number | undefined {
    const state = store.getState();

    const accessToken = state.auth.token?.access_token;

    if (!accessToken) return undefined;

    try {
      const decoded = jwt_decode<IAccessTokenDecoded>(accessToken);
      return decoded.exp && decoded.exp * 1000;
    } catch {
      return undefined;
    }
  },
  generateProfilePath: (
    userId: string | number,
    tab?: {
      walletId?: number | string;
      cardId?: number | string;
      kycKyb?: {};
      beneficiaryFilter?: Record<string, string | number | boolean>;
    },
  ): string => {
    const config = getStaticDashboardConfig();
    const PathRoute = getRoutepaths(config);

    const rootPath = generatePath(PathRoute.app.root, { '*': PathRoute.app.users.main });
    const profilePath = generatePath(`${rootPath}/${PathRoute.app.users.profile}`, { userId: `${userId}` });

    if (tab?.walletId)
      return generatePath(
        `${profilePath}/${PathRoute.app.users.tabs.wallets.main}/${PathRoute.app.users.tabs.wallets.wallet}`,
        { walletId: `${tab.walletId}` },
      );
    if (tab?.cardId)
      return generatePath(
        `${profilePath}/${PathRoute.app.users.tabs.cards.main}/${PathRoute.app.users.tabs.cards.card}`,
        { cardId: `${tab.cardId}` },
      );
    if (tab?.kycKyb) return `${profilePath}/${PathRoute.app.users.tabs.kyckyb}`;

    if (tab?.beneficiaryFilter) {
      const filter = Object.entries(tab.beneficiaryFilter)
        .map(([key, value]) => {
          return `${key}=${value.toString()}`;
        })
        .join('&');
      return generatePath(`${profilePath}/${PathRoute.app.users.tabs.beneficiaries.main}?${filter}`);
    }

    return profilePath;
  },
  getUserName: (user?: IUser): string | undefined => {
    if (!user) return '-';

    if (user?.userTypeId === UserTypes.naturalPerson && !user?.firstname && !user?.lastname) return undefined;

    if (user.userTypeId === UserTypes.naturalPerson) return `${user.firstname} ${user.lastname}`;

    return `${user.legalName}`;
  },

  textLine: (text?: string): string => {
    if (text) return `${text}\n`;
    return '';
  },

  getUserAddress: (user?: IUser): string | undefined => {
    if (!user) return '-';
    return `${DashboardHelpers.textLine(user?.address1)} ${DashboardHelpers.textLine(user?.address2)} ${DashboardHelpers.textLine(user?.address3)} ${user?.postcode || ''} ${user?.city || ''} - ${user?.country ? i18n.t(`shared.countries.${user.country}`) : ''}`;
  },

  getUserNameInitials: (user?: IUser): string => {
    if (!user) return '';

    if (user.userTypeId === UserTypes.naturalPerson && user.firstname && user.lastname)
      return `${user.firstname?.[0]}${user.lastname?.[0]}`.toUpperCase();
    return `${user.firstname?.slice(0, 2)}`.toUpperCase();
  },

  getUserNameAndInitials: (user: IUser): { name: string; initial: string } => {
    if (user.userTypeId === UserTypes.naturalPerson)
      return user.firstname || user.lastname
        ? {
            name: `${user.firstname} ${user.lastname}${selfEmployedLegalForms.includes(Number(user.legalForm) as TLegalFormKey) ? ` - ${user.legalName}` : ''}`,
            initial: `${user.firstname?.[0]}${user.lastname?.[0]}`.toUpperCase(),
          }
        : {
            name: i18n.t('pages.dashboard.users.physicalUserRole.anonymous'),
            initial: i18n
              .t('pages.dashboard.users.physicalUserRole.anonymous')
              .split(' ')
              .map((name) => name[0]?.toUpperCase())
              .join(''),
          };
    else
      return {
        name: user.legalName,
        initial: user.legalName?.[0]?.toUpperCase(),
      };
  },

  getFirstCapitalLetter: (inputString: string, number?: number): string => {
    const words = inputString
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, number ?? 2);

    return words
      .map((word) => {
        const firstChar = word[0];
        return firstChar && /[A-Z]/.test(firstChar) ? firstChar : '';
      })
      .filter(Boolean)
      .join('');
  },

  truncateText: (text: string, maxLength: number) => {
    if (text.length <= maxLength) {
      return text;
    }

    const ellipsis = '...';
    const visibleLength = Math.max(0, maxLength - ellipsis.length);

    return text.substring(0, visibleLength) + ellipsis;
  },

  getUserStatus: (user: IUser) => {
    const userStatus = user?.isFrozen === 1 || user?.isFreezed === 1 ? UserStatusLabel.BLOCKED : user.userStatus;
    return userStatus
      ? {
          status: userStatus,
          label: i18n.t(`pages.dashboard.users.status.${userStatus}`),
          color: statusColor[userStatus],
        }
      : undefined;
  },

  getUserRole: (user: IUser): TPhysicalUserRole => {
    if (user.userTypeId === UserTypes.naturalPerson) {
      if (selfEmployedLegalForms.includes(Number(user.legalForm) as TLegalFormKey))
        return physicalUserRole.selfEmployed;
      if (user.parentType === EParentType.employee) return physicalUserRole.employee;
      if (user.parentType === EParentType.leader && user.controllingPersonType === 3) {
        if (user.effectiveBeneficiary === 0) return physicalUserRole.legalRepresentative;
        if (user.effectiveBeneficiary > 0) return physicalUserRole.legalRepresentativeAndShareholder;
      }
      if (user.parentType === EParentType.shareholder && user.controllingPersonType === 1)
        return physicalUserRole.shareholder;
      if (
        (user.parentType === EParentType.leader || user.parentType === EParentType.shareholder) &&
        user.controllingPersonType === 0 &&
        user.birthday &&
        Math.abs(DateTools.difference(DateTools.now(), DateTools.createDate(user.birthday), 'year')) < 18
      )
        return physicalUserRole.minor;

      if (
        user.parentType === EParentType.leader &&
        user.controllingPersonType === 0 &&
        (!user.birthday ||
          !DateTools.format(user.birthday) ||
          (user.birthday &&
            Math.abs(DateTools.difference(DateTools.now(), DateTools.createDate(user.birthday), 'year')) > 18))
      )
        return physicalUserRole.child;

      if (!user.lastname && !user.firstname) return physicalUserRole.anonymous;
      return undefined;
    } else return undefined;
  },

  isDocumentReview: (document: IKycDocument): boolean =>
    document.metadata?.connectDocumentId === undefined && Boolean(document.isAgent),
  isCredit: (beneficiary: IBeneficiary) => Boolean(beneficiary.usableForSct),
  isB2B: (beneficiary: IBeneficiary) => Boolean(beneficiary.sddB2bWhitelist && beneficiary.sddB2bWhitelist?.length > 0),
  isB2C: (beneficiary: IBeneficiary) =>
    Boolean(!Array.isArray(beneficiary.sddB2bWhitelist) || beneficiary.sddB2bWhitelist?.length === 0),
  getBooleanRadioValues: (values?: (string | number | boolean)[]): TOption[] => [
    { label: i18n.t('translation:components.molecules.forms.no'), value: values?.[0] || 0 },
    { label: i18n.t('translation:components.molecules.forms.yes'), value: values?.[1] || 1 },
  ],
  getErrorMessage: (error: unknown): string => {
    if (Utils.isString(error)) return error;
    return (error as IApiError).errors?.[0]?.message || i18n.t('translation:pages.cores.errors.defaultAppError');
  },
  getPdfSize: (currentDoc: HTMLCanvasElement | HTMLImageElement, currentContainer: HTMLDivElement) => {
    const parentHeight = currentContainer.clientHeight;
    const parentWidth = currentContainer.clientWidth;
    const clientHeight = currentDoc.clientHeight;
    const clientWidth = currentDoc.clientWidth;
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);

    if (clientHeight >= parentHeight || clientWidth >= parentWidth) {
      const parentRatio = parentWidth / clientWidth;

      if (currentDoc instanceof HTMLCanvasElement) {
        currentDoc.style.width =
          clientWidth * parentRatio > parentWidth
            ? `${parentWidth / rootFontSize}rem`
            : `${(clientWidth * parentRatio) / rootFontSize}rem`;

        currentDoc.style.height =
          clientHeight * parentRatio > parentHeight
            ? `${parentHeight / rootFontSize}rem`
            : `${(clientHeight * parentRatio) / rootFontSize}rem`;
      } else {
        currentDoc.style.maxWidth = '90%';
        currentDoc.style.maxHeight = '90%';
      }
    }
  },
  getStatusColor: (status: string): TStatusColorKey => {
    switch (status) {
      case 'A':
        return 'ACTIVE';
      case 'S':
        return 'SUSPENDED';
      case 'D':
        return 'DEACTIVATED';
      case 'X':
        return 'DEACTIVATED';
      default:
        return 'ACTIVE';
    }
  },

  getStatusLabelTranslation: (label?: TStatusColorKey, status?: TStatusColorKey) =>
    label
      ? i18n.t(`translation:components.molecules.statusLabel.${label}`)
      : i18n.t('translation:components.molecules.statusLabel.NOT_IMPLEMENTED', {
          status: getStatusByLabel(status),
        }),

  downloadFile: (url: string, fileName: string, newTab?: boolean) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = newTab ? '_blank' : '_self';
    link.rel = 'noopener noreferrer';
    link.download = fileName;
    link.click();
  },
  getUtcDateTime: (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getUTCHours(), date.getUTCMinutes()),

  formatDateToApi: (date: Date) => {
    if (!(date instanceof Date)) {
      console.error('Invalid Date');
      return undefined;
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  },

  parseApiToDate: (dateString: string) => {
    if (!dateString) {
      return undefined;
    }
    return new Date(dateString);
  },

  flattenObject: (obj: object, prefix: string = '', displayValue: (value: string) => ReactNode) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        acc = { ...acc, ...DashboardHelpers.flattenObject(value, newKey, displayValue) };
      } else {
        acc[newKey] = displayValue(value);
      }

      return acc;
    }, {});
  },

  hasValue: (obj: object) => {
    if (!obj) return false;

    return Object.values(obj).some((value) => {
      if (typeof value === 'object' && value !== null) {
        return DashboardHelpers.hasValue(value);
      }
      return value !== null && value !== undefined && value !== '';
    });
  },
};
